import { useEffect, useState } from "react";
import {
  Card,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import "./details.css";
import EditClientDetails from "./edit-client-details/edit-client-details";
import EditContactDetails from "./edit-contact-details/edit-contact-details";
import EditSFSContactDetails from "./edit-sfs-contact-details/edit-sfs-contact-details";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeactivateClientDialog from "./deactivate-client/deactivate-client";
import ReActivateClientDialog from "./re-activate-client/re-activate-client";
import ContactLayout from "../../../../../components/contact-layout/contact-layout";

type props = {
  data: any;
};

export default function Details({ data }: props) {
  const [isActive, setIsActive] = useState(false);
  const [primaryPhone, setPrimaryPhone] = useState<any>();
  const [altPhones, setAltPhones] = useState<any>([]);
  const [primaryAccountManager, setPrimaryAccountManager] = useState<any>();
  const [altAccountManager, setAltAccountManager] = useState<any>([]);
  const [primaryAccountingContact, setPrimaryAccountingContact] =
    useState<any>();
  const [altAccountingContact, setAltAccountingContact] = useState<any>([]);
  const [primarySalesManager, setPrimarySalesManager] = useState<any>();
  const [altSalesManager, setAltSalesManager] = useState<any>([]);
  const [primaryRelationshipManager, setPrimaryRelationshipManager] =
    useState<any>();
  const [altRelationshipManager, setAltRelationshipManager] = useState<any>([]);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [showEditSFSContact, setShowEditSFSContact] = useState(false);
  const [showDeactivateClient, setShowDeactivateClient] = useState(false);
  const [showReActivateClient, setShowReActivateClient] = useState(false);

  useEffect(() => {
    if (data) {
      setIsActive(data?.isActive);
      const filterPrimaryPhones = data?.phones.filter(
        (phone: any) => phone.isPrimary === true
      );
      const filterAltPhones = data?.phones.filter(
        (phone: any) => phone.isPrimary === false
      );
      setPrimaryPhone(filterPrimaryPhones[0]);
      setAltPhones(filterAltPhones);

      const filterPrimaryAccountManager = data?.accountManagers.filter(
        (accountManager: any) => accountManager.isPrimary === true
      );
      const filterAltAccountManager = data?.accountManagers.filter(
        (accountManager: any) => accountManager.isPrimary === false
      );
      setPrimaryAccountManager(filterPrimaryAccountManager[0]);
      setAltAccountManager(filterAltAccountManager);

      const filterPrimaryAccountingContact = data?.accountingContacts.filter(
        (accountingContact: any) => accountingContact.isPrimary === true
      );
      const filterAltAccountingContact = data?.accountingContacts.filter(
        (accountingContact: any) => accountingContact.isPrimary === false
      );
      setPrimaryAccountingContact(filterPrimaryAccountingContact[0]);
      setAltAccountingContact(filterAltAccountingContact);

      const filterPrimarySalesManager = data?.salesManagers.filter(
        (salesManager: any) => salesManager.isPrimary === true
      );
      const filterAltSalesManager = data?.salesManagers.filter(
        (salesManager: any) => salesManager.isPrimary === false
      );
      setPrimarySalesManager(filterPrimarySalesManager[0]);
      setAltSalesManager(filterAltSalesManager);

      const filterPrimaryRelationshipManager =
        data?.relationShipManagers.filter(
          (relationShipManager: any) => relationShipManager.isPrimary === true
        );
      const filterAltRelationshipManager = data?.relationShipManagers.filter(
        (relationShipManager: any) => relationShipManager.isPrimary === false
      );
      setPrimaryRelationshipManager(filterPrimaryRelationshipManager[0]);
      setAltRelationshipManager(filterAltRelationshipManager);
    }
  }, [data]);

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    // setIsActive(isChecked);
    if (isChecked === false) {
      setShowDeactivateClient(true);
    } else {
      setShowReActivateClient(true);
    }
  };

  const handleEditClient = () => {
    setShowEditClient(true);
  };

  const handleEditContact = () => {
    setShowEditContact(true);
  };

  const handleEditSFSContact = () => {
    setShowEditSFSContact(true);
  };

  return (
    <>
      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Client Details</Typography>
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEditClient}
          >
            Edit
          </Button>
        </Box>
        <Box className="display-name-layout">
          <Typography className="label-txt">Status</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch checked={isActive} onChange={handleStatusChange} />
              }
              label={isActive ? "Active" : "Inactive"}
            />
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Client Name</Typography>
          <Typography>{data?.clientName}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              VIP Client
            </Typography>
            <Tooltip
              title="This setting is internal only and never visible to the client. If activated, all orders from this client will contain a flag so we know to give this order special attention."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography>{data?.isVip ? "Yes" : "No"}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Company ID</Typography>
          <Typography>{data?.companyId}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Website</Typography>
          <Box display={"flex"} alignItems={"center"}>
            <Typography sx={{ color: "#E35F00" }}>{data?.website}</Typography>
            <Button>
              <OpenInNewOutlinedIcon color="primary" />
            </Button>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Notify the Order
              <br />
              Contact About
              <br />
              Appointment
              <br />
              Updates?
            </Typography>
            <Tooltip
              title="When toggled 'on', an email will be sent to the assigned order contact whenever an inspector schedules or reschedules an appointment for that order."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography>
            {data?.notifyOnAppointmentUpdates ? "Yes" : "No"}
          </Typography>
        </Box>
      </Card>

      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Contact Details</Typography>
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEditContact}
          >
            Edit
          </Button>
        </Box>
        <Box className="display-name-layout">
          <Typography className="label-txt">Phone</Typography>
          <Box>
            {primaryPhone ? (
              <Box display={"flex"} alignItems="center">
                <Typography>{primaryPhone?.phone}</Typography>
                <Chip
                  sx={{ height: 24, ml: 1 }}
                  label="primary"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            ) : (
              <Box>-</Box>
            )}
            {altPhones?.map((altPhone: any, index: number) => (
              <Typography key={index}>{altPhone?.phone}</Typography>
            ))}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Mailing Address</Typography>
          {data?.mailingAddress ? (
            <Box>
              <Typography>{data?.mailingAddress?.street}</Typography>
              <Typography>{data?.mailingAddress?.address2}</Typography>
              <Typography>
                {data?.mailingAddress?.city}, {data?.mailingAddress?.state}{" "}
                {data?.mailingAddress?.zip}
              </Typography>
              <Typography>{data?.mailingAddress?.country}</Typography>
            </Box>
          ) : (
            <Box>-</Box>
          )}
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Billing Address</Typography>
          {data?.billingAddress ? (
            <Box>
              <Typography>{data?.billingAddress?.street}</Typography>
              <Typography>{data?.billingAddress?.address2}</Typography>
              <Typography>
                {data?.billingAddress?.city}, {data?.billingAddress?.state}{" "}
                {data?.billingAddress?.zip}
              </Typography>
              <Typography>{data?.billingAddress?.country}</Typography>
            </Box>
          ) : (
            <Box>-</Box>
          )}
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Account Manager/Admin</Typography>
          <Box>
            {primaryAccountManager ? (
              <ContactLayout data={primaryAccountManager} isPrimary={true} />
            ) : (
              <> - </>
            )}
            {altAccountManager.map((accountManager: any) => (
              <ContactLayout
                key={accountManager.userId}
                data={accountManager}
                isPrimary={false}
              />
            ))}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Accounting Contact</Typography>
          <Box>
            {primaryAccountingContact ? (
              <ContactLayout data={primaryAccountingContact} isPrimary={true} />
            ) : (
              <> - </>
            )}
            {altAccountingContact.map((accountingContact: any) => (
              <ContactLayout
                key={accountingContact.userId}
                data={accountingContact}
                isPrimary={false}
              />
            ))}
          </Box>
        </Box>
      </Card>

      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">SFS Contact Details</Typography>
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEditSFSContact}
          >
            Edit
          </Button>
        </Box>
        <Box className="display-name-layout">
          <Typography className="label-txt">Sales Manager</Typography>
          <Box>
            {primarySalesManager ? (
              <ContactLayout data={primarySalesManager} isPrimary={true} />
            ) : (
              <> - </>
            )}
            {altSalesManager.map((salesManager: any) => (
              <ContactLayout
                key={salesManager.userId}
                data={salesManager}
                isPrimary={false}
              />
            ))}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Relationship Manager</Typography>
          <Box>
            {primaryRelationshipManager ? (
              <ContactLayout
                data={primaryRelationshipManager}
                isPrimary={true}
              />
            ) : (
              <> - </>
            )}
            {altRelationshipManager.map((relationshipManager: any) => (
              <ContactLayout
                key={relationshipManager.userId}
                data={relationshipManager}
                isPrimary={false}
              />
            ))}
          </Box>
        </Box>
      </Card>

      {showEditClient && (
        <EditClientDetails
          showDialog={showEditClient}
          closeDialog={() => setShowEditClient(false)}
          data={data}
        />
      )}

      {showEditContact && (
        <EditContactDetails
          showDialog={showEditContact}
          closeDialog={() => setShowEditContact(false)}
          data={data}
        />
      )}

      {showEditSFSContact && (
        <EditSFSContactDetails
          showDialog={showEditSFSContact}
          closeDialog={() => setShowEditSFSContact(false)}
          data={data}
        />
      )}

      {showDeactivateClient && (
        <DeactivateClientDialog
          id={data?.id}
          name={data?.clientName}
          showDialog={showDeactivateClient}
          closeDialog={() => setShowDeactivateClient(false)}
        />
      )}

      {showReActivateClient && (
        <ReActivateClientDialog
          id={data?.id}
          showDialog={showReActivateClient}
          closeDialog={() => setShowReActivateClient(false)}
        />
      )}
    </>
  );
}
